import React, { useEffect, useRef, useState } from 'react'
import CheckboxListCard, { CheckboxItems } from '../../Card/CheckboxListCard'
import { useTranslation } from 'react-i18next'
import { useLoadingIds } from '../../../hooks/useLoadingIds'
import { useAppSnackbar } from '../../../hooks/useAppSnackbar'
import { Role } from '../../../swagger'
import { useNotificationContext } from '../../Context/NotificationContext'
import { meta } from '../../../api/swagger'
import { useMountEffect } from '../../../hooks/useMountEffect'
import useLoadingContext from '../../../hooks/useLoadingContext'

export const NotificationRolesCard: React.FC<{
  isEditMode?: boolean
}> = ({ isEditMode }) => {
  const { t } = useTranslation()
  const {
    Notification: { fetchRoles: fetchRolesLoadingId },
  } = useLoadingIds()

  const { handleError } = useAppSnackbar()
  const [roles, setRoles] = useState<Role[] | undefined>()
  const [checkboxItems, setCheckboxItems] = useState<CheckboxItems[]>([])
  const {
    notificationDetails,
    updateNotificationDetails,
    updateFormValidity,
    formValidity,
  } = useNotificationContext()
  const { roleKeys } = notificationDetails

  /** If a key is in this map, it's selected */
  const [roleKeysMap, setRoleKeysMap] = useState(() => {
    return new Map(roleKeys?.map((key) => [key, `${key}`]))
  })

  const AllRolesCheckboxItem = useRef<CheckboxItems>({
    name: t(
      'Notifications.NotificationRolesCard.Checkbox.AllRoles',
      'All Roles'
    ),
    key: -1,
    checked: roleKeysMap.has(-1),
  })

  const ParentCheckboxItem = useRef<CheckboxItems>({
    name: t('Notifications.NotificationRolesCard.Checkbox.Parent', 'Parent'),
    key: 0,
    checked: roleKeysMap.has(0),
  })

  useEffect(() => {
    if (!!notificationDetails.roleKeys && roleKeysMap.size === 0) {
      setRoleKeysMap(new Map(roleKeys?.map((key) => [key, `${key}`])))
      if (roleKeys.includes(0)) {
        ParentCheckboxItem.current = {
          ...ParentCheckboxItem.current,
          checked: true,
        }
      }
    }
  }, [notificationDetails, roleKeys, roleKeysMap.size])

  const fetchUserRoles = async () => {
    try {
      const roles = await meta.fetchRoles()
      setRoles(roles)
    } catch (e) {
      await handleError(
        e,
        t(
          'Admin.NotificationRolesCard.FetchRoles.FailedToFetch',
          'An unknown error occurred fetching roles.'
        )
      )
    }
  }

  useEffect(() => {
    setCheckboxItems(
      [
        ParentCheckboxItem.current,
        ...(roles ?? []).map(({ roleKey, name }) => {
          return {
            name,
            key: roleKey as number,
            checked: roleKeysMap.has(roleKey as number),
          }
        }),
      ].sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
    )
  }, [roleKeysMap, roles, ParentCheckboxItem])

  const { triggerFetch } = useLoadingContext({
    asyncFunction: fetchUserRoles,
    loadingId: fetchRolesLoadingId,
  })

  useMountEffect(() => {
    triggerFetch()
  })

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target

    if (roleKeysMap.has(+value)) {
      roleKeysMap.delete(+value)
    } else {
      roleKeysMap.set(+value, value)
    }
    if (value === `-1`) {
      AllRolesCheckboxItem.current = {
        ...AllRolesCheckboxItem.current,
        checked: !AllRolesCheckboxItem.current.checked,
      }

      ParentCheckboxItem.current = {
        ...ParentCheckboxItem.current,
        checked: AllRolesCheckboxItem.current.checked,
      }

      const newMap = AllRolesCheckboxItem.current.checked
        ? new Map((roles ?? []).map(({ roleKey }) => [roleKey, `${roleKey}`]))
        : new Map()

      if (ParentCheckboxItem.current.checked) {
        newMap.set(0, '0')
      } else {
        newMap.delete(0)
      }

      setRoleKeysMap(newMap)
      updateNotificationDetails({
        ...notificationDetails,
        roleKeys: Array.from(newMap.keys()).map((key) =>
          key === 0 ? null : key
        ),
      })
    } else if (value === '0') {
      ParentCheckboxItem.current = {
        ...ParentCheckboxItem.current,
        checked: !ParentCheckboxItem.current.checked,
      }

      setRoleKeysMap(roleKeysMap)
      updateNotificationDetails({
        ...notificationDetails,
        roleKeys: Array.from(roleKeysMap.keys()),
      })
    } else {
      setRoleKeysMap(roleKeysMap)
      updateNotificationDetails({
        ...notificationDetails,
        roleKeys: Array.from(roleKeysMap.keys()),
      })
    }

    setCheckboxItems(
      [
        {
          ...ParentCheckboxItem.current,
        },
        ...(roles ?? []).map(({ roleKey, name }) => {
          return {
            name,
            key: roleKey as number,
            checked: value === `-1` ? true : roleKeysMap.has(roleKey as number),
          }
        }),
      ].sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
    )

    updateFormValidity({
      ...formValidity,
      // Technically we can provide 0 length but it makes validation harder.
      regions: {
        input: true,
      },
    })
  }

  return (
    <CheckboxListCard
      checkboxItems={checkboxItems}
      isFieldDisabled={!isEditMode}
      headerName={t(
        'Admin.Notifications.NotificationRolesCard.Header',
        'Roles'
      )}
      headerId={'notification-roles-header'}
      handleCheckboxChange={handleCheckboxChange}
      alertMessage={
        !formValidity.roles.input
          ? t(
              'Admin.Notifications.CheckboxListCard.RolesValidition',
              'Please select one or more roles.'
            )
          : undefined
      }
      headerCheckbox={AllRolesCheckboxItem.current}
    />
  )
}
